import { Box, Button, CircularProgress, Menu, MenuItem, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Map } from '@opengov/compose-app/immutable';

import { getDisplayNameForNodeId } from '@opengov/component-library/capital';

import { useReportsForEntity } from '../../../../../modals/components/AddTile/hooks/useReportsForEntity';
import { usePublicationById } from '../hooks/usePublicationById';
import { usePublicationFolderStructure } from '../hooks/usePublicationFolderStructure';
import {
  useExportPublicationPdf,
  useMoveSection,
  usePreviewPublicationPdf,
  useSetPublicationThumbnail
} from '../hooks/usePublicationMutations';
import {
  PublicationStoriesForbiddenError,
  PublicationStoriesKeyParams,
  usePublicationStories
} from '../hooks/usePublicationStories';
import { useTemplates } from '../hooks/useTemplates';
import { TemplatesKeyParams } from '../hooks/publicationViewQueryKeys';
import { useToast } from '../../../Toast/context/useToast.context';
import { EMPTY_SETTINGS } from '../../../StoriesSettings/emptySettings';
import { useFetchSettings } from '../../../StoriesSettings/hooks/useFetchSettings';
import {
  DEFAULT_PER_PAGE,
  PATH_CREATE_FROM_TEMPLATE,
  PUBLICATIONS_MANAGER_PRIVILEGE,
  TAB_PUBLICATIONS,
  TAB_TEMPLATES,
  SHOW,
  SHOW_ALL,
  SHOW_USER,
  SORT,
  SORT_CREATED_NEW_TO_OLD,
  SORT_CREATED_OLD_TO_NEW,
  SORT_CUSTOM,
  SORT_NAME_A_Z,
  SORT_NAME_Z_A,
  SORT_UPDATED_NEW_TO_OLD,
  SORT_UPDATED_OLD_TO_NEW,
  STATE_ALL,
  STATE_PUBLISHED,
  STORIES_ADMIN_PRIVILEGE,
  STORIES_CREATE_PRIVILEGE,
  STORIES_VIEWER_PRIVILEGE,
  STORY_VISIBILITY,
  STORY_ACCESS,
  TAB_REPORTS,
  TAB_STORIES,
  TAB_CUSTOM_REPORTS,
  VIEW,
  VIEW_GRID,
  VIEW_LIST
} from '@opengov/stories-shared/constants';
import {
  AsyncJobStatus,
  IPublication,
  IStory
} from '@opengov/stories-shared/types';
import { isEmpty } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Cookies from 'universal-cookie';

import {
  EProcessStatus,
  ESelectAllStatus,
  IExportPdfProcess,
  IExportPublicationPdfProcess,
  IFolderCopyProcess,
  IModalMessages,
  IMoveSectionGraphqlRequest,
  IPreviewPdfFolderRequest,
  IPreviewPublicationPdfRequest,
  IPublicationModalMessages,
  IPublicationViewMessages,
  ISessionUser,
  ISortOptions,
  IStoriesCopyProcess,
  IStoriesFolder,
  IStoryCookie,
  IStoryListing,
  IStoryListMessages,
  IStoryListSelection,
  IStoryShowOptions,
  IStoryViewOptions,
  IStoryVisibilityOptions,
  ITabOptions,
  IsubjectClaimsMessages,
  IStoryPrivilege
} from '../../../../../types';
import { generateOnlinePublicationUrl } from '../../../../utils/generateOnlinePublicationUrl';
import {
  bulkChangeStatusSubscriptionQuery,
  copyPublicationSubscriptionQuery,
  folderCopyProcessesSubscriptionQuery,
  getExportPdfDetailsForFolder,
  getExportPdfDetailsForPublication,
  storiesCopyProcessesSubscriptionQuery
} from '../../../../graphQL/queries';
import {
  navigateToOrganizeSectionAndStoriesPath,
  navigateToPublicationCustomReportsView,
  navigateToPublicationFolderList,
  navigateToPublicationReportsView,
  navigateToPublicationView, navigateToSectionSetupPDF
} from '../../../../state/navigation';

import {
  getNewCookieValue,
  HasuraSubscription,
  LONG_POLL_INTERVAL_SHORT,
  MessageListener
} from '../../../../utils';

import {
  useFoldersAndSubFoldersStoryCount,
  usePreviewFolderPdf,
  usePublicationFoldersTree,
  useStoryFolders
} from '../../../StoryList/hooks';
import {
  useCurrentSubjectClaims,
  useSubjectPublicationStoryClaim
} from '../../../../hooks/subjectClaims';
import { alphabetizeNodes, compileNodes, compilePublicationNodes } from '../../../../utils/helper';
import { ControlBar } from '../../../StoryList/ControlBar';
import { CopyFolderModal } from '../../../StoryList/CopyFolderModal';
import { CreateStoryFromTemplateModal } from '../../../StoryList/CreateStoryFromTemplateModal';
import { ExportPdfModal } from '../../../StoryList/ExportPdfModal/ExportPdfModal';
import { MoveFolderModal } from '../../../StoryList/MoveFolderModal/MoveFolderModal';
import { SelectionToolbar } from '../../../StoryList/SelectionToolbar';
import { CUSTOM_REPORTS, REPORTS, STORIES } from '../../../StoryList/Sidebar/Sidebar';
import { exportPdfMessage, formatCreatedAt, StoryListBanner } from '../../../StoryList/StoryListBanner';
import { StoryListTable } from '../../../StoryList/StoryListContent/StoryListTable';
import { StoryCardGrid } from '../../../StoryList/StoryListContent/StoryCardGrid';
import { AddSectionModal } from '../../AddSectionModal/AddSectionModal';
import { PublicationDeleteModal } from '../../PublicationDeleteModal';
import { UpdatePublicationSectionModal } from '../../UpdatePublicationSectionModal/UpdatePublicationSectionModal';
import { PublicationViewHeader } from '../Header/PublicationViewHeader';
import { styles } from './PublicationView.styles';
import { getLocalStorageParsedValue, setLocalStorageParsedValue } from '../../../../utils/localStorageUtils';
import { StoriesListEmptyView } from '../../../CommonComponents/StoriesListEmptyView';
import { PublicationSidebarContent } from '../PublicationSidebarContent/PublicationSidebarContent';
import { PublicationReportEmbedContent } from '../PublicationReportEmbedContent';
import { EmptyViewContent } from '../../../CommonComponents/EmptyViewContent/EmptyViewContent';
import { PageErrorBoundary } from '../../../ErrorBoundary/PageErrorBoundary';
import { ViewName } from '../../../../../instrumentation/views';
import { StoriesSelectionProvider, useStoriesSelectionContext } from '../context/StoriesSelectionContext';
import { sendPublicationViewedActivity } from '../../../../activity/sendPublicationViewedActivity';
import { WorkspaceSidebarProvider } from '@opengov/bnp-component-workflows-widgets';
import { CustomReportsBoundary } from '../../CustomReports/CustomReports';
import { PublicationViewLoadingSkeleton } from './PublicationViewLoadingSkeleton';

const SLOW_FADE_HIGHLIGHT_TIMEOUT = 7000;

export interface IPublicationsViewProps {
  showGlobalNavBar: boolean;
  messages: IPublicationViewMessages;
  subjectClaimsMessages: IsubjectClaimsMessages;
  isOpenGovAdmin: boolean;
  entityId: number;
  entityUuid: string;
  entitySubdomain: string;
  entityName: string;
  publicationId: string;
  sectionId: string;
  canOrganizeStories: boolean;
  storyMessages: IStoryListMessages;
  publicationModalMessages: IPublicationModalMessages;
  selectAllStories?: boolean;
  // publicationFolders + publicationFoldersTree removed — component fetches via usePublicationFolderStructure hook
  // folderStructure + rootFolderId removed — component fetches via useStoryFolders hook
  user: ISessionUser;
  lastPath: string;
  modalMessages: IModalMessages;
  storyMarginsEnabled: boolean;
  copyPublicationJobId?: string;
  displayReportEmbeds: boolean;
  isPublicationCustomCssEnabled: boolean;
  isPublicationSetupPdfEnabled: boolean;
  isBulkUpdateStoryStatusEnabled: boolean;
  isReportEmbedListSelected: boolean;
  enableGoogleTranslateForPublications: boolean;
  selectedSectionLabel: string;
  bulkChangeStatusJobId?: string;
  isCustomSpreadsheetReportEnabled: boolean;
  isFormsAndWorkflowsFeatureEnabled: boolean;
  isPerformanceManagementFeatureEnabled: boolean;
  navigateToPublicationList(): void;
  navigateToPublicationStoryView(storyId: string, isTemplate: boolean, publicationId: string): void;
  navigateToPublicationSectionList(publicationId: string, sectionId: string): void;
  navigateToPublicationStoriesList(publicationId: string): void;
  navigateToSetupOnlinePublication(publicationId: string): void;
  navigateToPublicationSetupPDF(publicationId: string): void;
  openModal(modal: string): IAction<string, string>;
  storiesAccessControlEnabled: boolean;
  canFollowStories?: boolean;
  isNewReportTileModalEnabled: boolean;
}

const cookies = new Cookies();

// Stable empty listing for the declarative stories/templates
// queries before data arrives (replaces the always-present immutable default
// the `publicationViewState.stories`/`templates` selector produced).
const EMPTY_STORY_LISTING = { data: [], total: 0, storiesUrl: '' } as unknown as IStoryListing;

const copyFolderSubscription = new HasuraSubscription({
  query: folderCopyProcessesSubscriptionQuery,
  longPollInterval: LONG_POLL_INTERVAL_SHORT
});

const exportPdfSubscription = new HasuraSubscription({
  query: getExportPdfDetailsForFolder
});

const exportPublicationPdfSubscription = new HasuraSubscription({
  query: getExportPdfDetailsForPublication
});

const copyStoriesSubscription = new HasuraSubscription({
  query: storiesCopyProcessesSubscriptionQuery,
  longPollInterval: LONG_POLL_INTERVAL_SHORT
});

const copyPublicationSubscription = new HasuraSubscription({
  query: copyPublicationSubscriptionQuery,
  longPollInterval: LONG_POLL_INTERVAL_SHORT
});

const bulkStatusChangeSubscription = new HasuraSubscription({
  query: bulkChangeStatusSubscriptionQuery
});

// TODO: Complete bulk-select migration
// 1. Remove `selectAllStories` from props (line 286) → use `useStoriesSelectionContext()`
// 2. Merge local `selectedStories` state (line 573) into context
// 3. Update SelectionToolbar + StoryListTable to use context
// 4. Remove from route selector (publicationViewRoute.ts)
// See: hooks/STORIES_SELECTION_MIGRATION.md for full guide

const PublicationViewInternal = (props: IPublicationsViewProps) => {
  const {
    isOpenGovAdmin,
    entityId,
    entitySubdomain,
    publicationId,
    messages,
    subjectClaimsMessages,
    // publicationFolders + publicationFoldersTree removed - using hook data below
    // folderStructure + rootFolderId removed - using useStoryFolders hook below
    storyMessages,
    publicationModalMessages,
    user,
    canOrganizeStories,
    modalMessages,
    lastPath,
    entityName,
    storyMarginsEnabled,
    displayReportEmbeds,
    isPublicationCustomCssEnabled,
    isPublicationSetupPdfEnabled,
    isBulkUpdateStoryStatusEnabled,
    enableGoogleTranslateForPublications,
    selectedSectionLabel,
    entityUuid,
    showGlobalNavBar,
    storiesAccessControlEnabled,
    isCustomSpreadsheetReportEnabled,
    isFormsAndWorkflowsFeatureEnabled,
    isPerformanceManagementFeatureEnabled,
    isNewReportTileModalEnabled,
    navigateToPublicationList,
    navigateToPublicationStoryView,
    navigateToSetupOnlinePublication,
    openModal,
    navigateToPublicationSetupPDF,
    canFollowStories
  } = props;

  const { data: currentSubjectClaims } = useCurrentSubjectClaims(
    entityUuid && user?.uuid ? { userUuid: user.uuid, entityUuid } : null
  );
  const publicationStoryClaimParams =
    publicationId && user?.uuid && entityUuid
      ? {
        publicationId,
        subjectId: user.uuid,
        entityUuid,
        storiesAccessControlEnabled
      }
      : null;
  const publicationStoryClaimQuery = useSubjectPublicationStoryClaim(publicationStoryClaimParams);
  const publicationStoryClaimResult = publicationStoryClaimQuery.data;
  // The retired route `onEnter` awaited the claims fetch before `onSuccess()`,
  // so the first story fetch always carried a resolved `isAdmin`. Gate the
  // stories query the same way — otherwise it races the claims query and an
  // admin's first request goes out with `is_admin: false`, rendering the
  // access-filtered subset until the corrected key refetches.
  //
  // "Settled" also covers the two ways the claims query never resolves, so the
  // stories query can never be blocked indefinitely: the query being disabled
  // (no params), and an errored fetch — the retired effect toasted and carried
  // on in that case rather than withholding the list.
  const areStoryClaimsSettled =
    publicationStoryClaimParams === null ||
    publicationStoryClaimQuery.isSuccess ||
    publicationStoryClaimQuery.isError;
  const currentSubjectPublicationStoryClaim = publicationStoryClaimResult?.allClaims ?? null;
  const sectionIds = publicationStoryClaimResult?.sectionIds ?? [];
  const isAdmin =
    Array.isArray(currentSubjectPublicationStoryClaim) &&
    currentSubjectPublicationStoryClaim.includes(STORIES_ADMIN_PRIVILEGE);

  const selectionCtx = useStoriesSelectionContext();
  const bulkChangeStatusJobId = selectionCtx.bulkChangeStatusJobId;
  const selectAllStories = selectionCtx.selectAll;

  // PDF preview + move-section migrated from compose-app effect bridges
  // to mutation hooks. copyPublicationJobId, exportPdfProcessStatus, copyFolderProcessStatus,
  // storiesCopyProcessStatus seeded from route props removed — local state initialises to
  // null / [] and subscriptions drive updates (no cold-start regression: the subscriptions
  // refire on re-mount before any user action needs the status).
  const previewPublicationPdfMutation = usePreviewPublicationPdf();
  const moveSectionMutation = useMoveSection();
  const getPublicationPdfPreviewLink = (previewProps: IPreviewPublicationPdfRequest) =>
    previewPublicationPdfMutation.mutateAsync(previewProps as unknown as Parameters<typeof previewPublicationPdfMutation.mutateAsync>[0]);
  const isPreviewingPublicationPdf = previewPublicationPdfMutation.isLoading;
  const isPublicationPreviewingPdfFailure = previewPublicationPdfMutation.isError;
  const previewPublicationPdfError = previewPublicationPdfMutation.error?.message ?? '';

  const previewFolderPdfMutation = usePreviewFolderPdf({
    title: modalMessages.somethingWentWrong
  });
  const getPdfPreviewLink = (previewProps: IPreviewPdfFolderRequest) =>
    previewFolderPdfMutation.mutateAsync({
      ...previewProps,
      entityId,
      entitySubdomain
    });
  const isPreviewingPdf = previewFolderPdfMutation.isLoading;
  const isPreviewingPdfFailure = previewFolderPdfMutation.isError;
  const previewFolderPdfError = previewFolderPdfMutation.error?.message ?? '';

  const { data: storyFoldersData, refetch: refetchStoryFolders } = useStoryFolders(
    entityId > 0 ? { entityId, displayPublicationTab: true } : null,
    messages.somethingWentWrongError
  );
  const rawStoryFolders = storyFoldersData?.storyFolders ?? [];
  const folderStructure: IStoriesFolder[] = useMemo(
    () => alphabetizeNodes(compileNodes(rawStoryFolders[0]?.subFolders) || []),
    [rawStoryFolders]
  );
  const rootFolderId: string = rawStoryFolders[0]?.id ?? null;

  const moveSections = async (moveSectionsProps: IMoveSectionGraphqlRequest) => {
    await moveSectionMutation.mutateAsync(moveSectionsProps);
    void refetchStoryFolders();
  };
  const copyPublicationJobId = props.copyPublicationJobId ?? '';

  const publicationCookie = cookies.get('publications') as IStoryCookie;

  // Fetch settings directly (react-query caches per entityId); EMPTY_SETTINGS
  // covers the brief cold-load window before the fetch resolves.
  const { data: fetchedSettings } = useFetchSettings({ entityId });
  const settings = fetchedSettings ?? EMPTY_SETTINGS;
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [rowsPerPageReportEmbed, setRowsPerPageReportEmbed] = useState(20);



  // Reports for the embedded-reports view. Replaces the compose-app
  // reporting-legacy-compose prefetch + selector. The entityId > 0
  // guard mirrors COMPOSE_RETIREMENT_GUIDELINES §9 — on refresh the store is
  // briefly unhydrated (entityId === 0) before onEnter resolves the entity.
  const reportsQuery = useReportsForEntity(entityId > 0 ? { entityId, includeAccountSummaryReports: true } : null);
  const reports = reportsQuery.data ?? [];

  // Toasts via useToast (§8) instead of the compose-app
  // `viewActionCreators.showCapitalToast`.
  const { toggleToast } = useToast();

  // The selected section id is now local state instead of
  // `publicationViewState.selectedSectionId`. It is seeded from the `sectionId`
  // route-match param and re-synced whenever the URL changes (route navigation
  // / browser back-forward), mirroring what the route `onEnter`'s
  // `setSelectedSectionId(sectionId)` dispatch did. `setSelectedSection`
  // updates it directly so the UI reacts immediately without a remount.
  const [selectedSectionId, setSelectedSectionIdState] = useState<string | null>(props.sectionId || null);
  useEffect(() => {
    setSelectedSectionIdState(props.sectionId || null);
  }, [props.sectionId]);

  // Publication metadata now comes from `usePublicationById`
  // instead of the compose-app `publicationViewState.publication` selector prop.
  // `entityId > 0` is the store-hydration guard (COMPOSE_RETIREMENT_GUIDELINES §9);
  // `publicationId` is the raw route-match param sourced by the route selector.
  const publicationByIdParams = publicationId && entityId > 0 ? { publicationId } : null;
  const publicationByIdQuery = usePublicationById(publicationByIdParams, messages.somethingWentWrongError);
  const publication = (publicationByIdQuery.data ?? {}) as IPublication;

  // The section/folder hierarchy now comes from
  // `usePublicationFolderStructure` (wraps `usePublicationFoldersHierarchy` +
  // the compile/alphabetize/sort transform) instead of `publicationViewState`.
  // `sectionIds` is the subjectClaims access-control filter (bridge);
  // it is part of the query key, so when `onEnter`'s claims fetch resolves and
  // updates it, the hierarchy refetches with the correct subtree.
  const foldersHierarchyParams =
    publicationId &&
      entityId > 0 &&
      sectionIds != null &&
      (sectionIds === 'All' || (Array.isArray(sectionIds) && sectionIds.length > 0))
      ? { publicationId, entityId, sectionIds }
      : null;
  const foldersHierarchyQuery = usePublicationFolderStructure(foldersHierarchyParams, messages.failedToGetSections);
  const { publicationFolderStructure, publicationRootFolderId } = foldersHierarchyQuery;
  const isFoldersLoading = foldersHierarchyParams !== null && foldersHierarchyQuery.isLoading;

  // All-publications folder tree for the copy-destination pickers.
  // CopyFolderModal needs the raw IPublicationFolder[] (`.storyFolder` shape);
  // CopyStoriesModal needs it compiled to an IStoriesFolder[] tree. Mirrors StoryList.
  const { data: rawPublicationFolders } = usePublicationFoldersTree(
    entityId > 0 ? { entityId } : null,
    messages.somethingWentWrongError
  );
  const publicationFolders = useMemo(() => rawPublicationFolders ?? [], [rawPublicationFolders]);
  const publicationFoldersTree: IStoriesFolder[] = useMemo(() => {
    if (!publicationFolders.length) return [];
    const roots = publicationFolders.filter(
      (f) => f.storyFolder.title.includes('root-folder') && (f.storyFolder.subFolders?.length ?? 0) > 0
    );
    return alphabetizeNodes((compilePublicationNodes(roots) || []) as IStoriesFolder[]);
  }, [publicationFolders]);
  const currentSectionName = getDisplayNameForNodeId(selectedSectionId, publicationFolderStructure);

  // Publication/thumbnail/export writes via mutation hooks
  // (usePublicationMutations) instead of the compose-app `publicationViewEffects`
  // prop callbacks. Title/section rename writes now live inside their own
  // modals (UpdatePublicationModal / UpdatePublicationSectionModal). Toasts are
  // emitted by the hooks (§8) with the same copy the effects used.
  const setPublicationThumbnailMutation = useSetPublicationThumbnail();
  const exportPublicationPdfMutation = useExportPublicationPdf({
    title: messages.somethingWentWrongError
  });

  const updateThumbnail = (thumbnailPublicationId: string, _controller: AbortController) => {
    void setPublicationThumbnailMutation.mutateAsync({
      publicationId: thumbnailPublicationId,
      entityId,
      entitySubdomain
    });
  };

  const [isCreatePublicationModalVisible, setIsCreatePublicationModalVisible] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [show, setShow] = useState<IStoryShowOptions>(publicationCookie?.show || SHOW_ALL);
  const [sort, setSort] = useState<ISortOptions>(selectedSectionId ? SORT_CUSTOM : (publicationCookie?.sort || SORT_UPDATED_NEW_TO_OLD));
  const [storyVisibility, setStoryVisibility] = useState<IStoryVisibilityOptions>(
    publicationCookie?.storyVisibility || STATE_ALL
  );
  const [storyAccess, setStoryAccess] = useState<IStoryPrivilege[]>(() => {
    if (publicationCookie?.storyAccess) {
      try {
        const parsedAccess =
          typeof publicationCookie.storyAccess === 'string'
            ? JSON.parse(publicationCookie.storyAccess)
            : publicationCookie.storyAccess;
        return parsedAccess;
      } catch (e) {
        console.error('Error parsing storyAccess from cookie:', e);
        return [STORIES_VIEWER_PRIVILEGE];
      }
    }
    return [STORIES_VIEWER_PRIVILEGE];
  });
  const filterSelected = search !== '' || show !== SHOW_ALL || storyVisibility !== STATE_ALL;
  const [pageHeader, setPageHeader] = useState<string>(messages.allPublicationStories);
  const [view, setView] = useState(VIEW_LIST);
  const [showReports, setShowReports] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
  const [isCreateFromTemplateModalVisible, setIsCreateFromTemplateModalVisible] = useState<boolean>(false);
  const [selectedTemplate, setSelectedTemplate] = useState<IStory>(null);
  const [folderCopyProcess, setFolderCopyProcess] = useState<IFolderCopyProcess | null>(null);
  const [isCopyFolderModalVisible, setIsCopyFolderModalVisible] = useState(false);
  const [isMoveFolderModalVisible, setIsMoveFolderModalVisible] = useState(false);
  const [allStoriesCount, setAllStoriesCount] = useState(0);
  const [isExportPdfModalVisible, setIsExportPdfModalVisible] = useState(false);
  const isLastPathCreateTemplate = lastPath === PATH_CREATE_FROM_TEMPLATE;
  const tab: ITabOptions = isLastPathCreateTemplate ? TAB_TEMPLATES : TAB_PUBLICATIONS;

  // Stories + templates are now declarative TanStack queries
  // keyed on a useMemo params object, replacing the imperative getStories /
  // getTemplates effect props + the getPublicationStoriesInternal debounce
  // machinery + the param-watching useEffect. Filter / section / sort /
  // pagination state are part of the query key, so any change refetches
  // (TanStack discards stale-key results — replacing the manual AbortController
  // de-dupe). subjectClaims `storyAccess`/`isAdmin` are forwarded exactly as the
  // retired effect did (bridge). Params are null (query disabled) on
  // the create-template view, before the publication/entity resolve, or before
  // the `entityId > 0` store-hydration guard (§9) passes.
  const storiesParams: PublicationStoriesKeyParams | null = useMemo(() => {
    if (!publication?.id || entityId <= 0 || !entityUuid || isLastPathCreateTemplate) {
      return null;
    }
    // Hold until subjectClaims resolve so `isAdmin` / `storyAccess` are correct
    // on the first request (see `areStoryClaimsSettled`).
    if (!areStoryClaimsSettled) {
      return null;
    }
    const currentOffset = currentPage > 1 ? rowsPerPage * (currentPage - 1) : 0;
    let params: PublicationStoriesKeyParams = {
      publicationId: publication.id,
      limit: rowsPerPage,
      offset: currentOffset,
      entityId,
      page: currentPage,
      sectionId: selectedSectionId || undefined,
      searchText: search,
      userUuid: user.uuid,
      entityUuid,
      storyState: storyVisibility,
      storiesAccessControlEnabled,
      isAdmin,
      storyAccess: storyAccess.filter((access) => access !== STORIES_VIEWER_PRIVILEGE)
    };
    if (show === SHOW_USER) {
      params = { ...params, userId: user.id };
    }
    switch (sort) {
      case SORT_UPDATED_OLD_TO_NEW:
        params = { ...params, updatedAt: 'asc' };
        break;
      case SORT_UPDATED_NEW_TO_OLD:
        params = { ...params, updatedAt: 'desc' };
        break;
      case SORT_NAME_Z_A:
        params = { ...params, orderByTitle: 'desc' };
        break;
      case SORT_NAME_A_Z:
        params = { ...params, orderByTitle: 'asc' };
        break;
      case SORT_CREATED_OLD_TO_NEW:
        params = { ...params, createdAt: 'asc' };
        break;
      case SORT_CREATED_NEW_TO_OLD:
        params = { ...params, createdAt: 'desc' };
        break;
      case SORT_CUSTOM:
        params = { ...params, order: 'asc', orderByTitle: 'asc' };
        break;
      default:
        params = { ...params, updatedAt: 'asc' };
        break;
    }
    return params;
  }, [
    publication?.id,
    entityId,
    entityUuid,
    isLastPathCreateTemplate,
    areStoryClaimsSettled,
    currentPage,
    rowsPerPage,
    selectedSectionId,
    search,
    user.uuid,
    user.id,
    storyVisibility,
    storiesAccessControlEnabled,
    isAdmin,
    storyAccess,
    show,
    sort
  ]);
  const storiesQuery = usePublicationStories(storiesParams, messages.somethingWentWrongError);
  const stories = storiesQuery.data ?? EMPTY_STORY_LISTING;

  const templatesParams: TemplatesKeyParams | null = useMemo(() => {
    if (!isLastPathCreateTemplate || entityId <= 0) {
      return null;
    }
    return {
      isOpenGovAdmin,
      entityId,
      ogId: user.id,
      page: currentPage,
      perPage: DEFAULT_PER_PAGE,
      shouldFilterByUser: show === SHOW_USER,
      sort: sort === SORT_CUSTOM ? SORT_UPDATED_NEW_TO_OLD : sort,
      title: search
    };
  }, [isLastPathCreateTemplate, entityId, isOpenGovAdmin, user.id, currentPage, show, sort, search]);
  const templatesQuery = useTemplates(templatesParams, messages.somethingWentWrongError);
  const templates = templatesQuery.data ?? EMPTY_STORY_LISTING;

  const foldersStoryCountParams = useMemo(
    () => (selectedSectionId && entityId > 0 ? { entityId, folderId: selectedSectionId } : null),
    [selectedSectionId, entityId]
  );
  const foldersStoryCountQuery = useFoldersAndSubFoldersStoryCount(
    foldersStoryCountParams,
    messages.somethingWentWrongError
  );

  // Forbidden (403) → navigate to the publication list, mirroring the retired
  // `getStories` effect (the hook throws PublicationStoriesForbiddenError and
  // does not toast it).
  useEffect(() => {
    if (storiesQuery.error instanceof PublicationStoriesForbiddenError) {
      navigateToPublicationList();
    }
  }, [storiesQuery.error]);

  const isStoriesLoading =
    (storiesParams !== null && storiesQuery.isLoading) ||
    // Claims still in flight — `storiesParams` is deliberately null, so keep the
    // spinner up instead of flashing the empty-list state before the fetch starts.
    (!areStoryClaimsSettled && !!publication?.id && entityId > 0 && !!entityUuid && !isLastPathCreateTemplate);
  const isLoading = publicationByIdParams !== null && publicationByIdQuery.isLoading;
  const publicationStoriesCount = stories.total;

  const totalStoriesCount = stories.total;
  const publicationFolderStructureLength = publicationFolderStructure?.length || 0;
  const showStoriesMessage = !totalStoriesCount && publicationFolderStructureLength;
  const [exportPdfProcess, setExportPdfProcess] = useState<IExportPdfProcess | null>(null);
  const [exportPublicationPdfProcess, setExportPublicationPdfProcess] = useState<IExportPublicationPdfProcess | null>(null);
  const [isPublicationExportPdfModalVisible, setIsPublicationExportPdfModalVisible] = useState(false);
  const [copyPublicationProcessStatus, setCopyPublicationProcessStatus] = useState<string>(null);
  const [shouldNavigateToCopiedPublication, setShouldNavigateToCopiedPublication] = useState(false);
  const [bulkChangeStatusProcessStatus, setBulkChangeStatusProcessStatus] = useState<string>(null);
  const [selectedStories, setSelectedStories] = useState<Map<string, IStory>>(Map([]));
  const [selectAllStatus, setSelectAllStatus] = useState(ESelectAllStatus.None);
  const [isUpdatePublicationSectionModalVisible, setUpdatePublicationSectionModalVisible] = useState(false);
  const [isSectionDeleteAction, setIsSectionDeleteAction] = useState<boolean>(false);
  const [highlightedStoriesIds, setHighlightedStoriesIds] = useState<string[]>([]);
  const [manageSectionAnchorEl, setManageSectionAnchorEl] = useState<null | HTMLElement>(null);
  const highlightFadeTimeout = useRef<ReturnType<typeof setTimeout>>();
  const [storiesCopyProcesses, setStoriesCopyProcesses] = useState<IStoriesCopyProcess[]>([]);
  const addCountOfEachFoldersStory = (allStories) => {
    let totalCount = 0;
    const addStoriesCount = (storiesData) => {
      storiesData.forEach((story) => {
        totalCount += story?.stories_count?.aggregate?.count;
        if (story.subFolders && story.subFolders.length) {
          addStoriesCount(story.subFolders);
        }
      });
    };
    addStoriesCount(allStories);
    return totalCount;
  };

  const updateHeaderText = () => {
    let header;
    if (showReports) {
      header = messages.allPublicationReports;
    } else if (isLastPathCreateTemplate) {
      header = storyMessages.allTemplates;
    } else if (selectedSectionId) {
      header = currentSectionName;
    } else {
      header = messages.allPublicationStories;
    }
    setPageHeader(header);
  };


  useEffect(() => {
    setShowReports(selectedSectionLabel === REPORTS);
  }, [selectedSectionLabel]);

  useEffect(() => {
    if (foldersStoryCountQuery.data) {
      setAllStoriesCount(addCountOfEachFoldersStory(foldersStoryCountQuery.data));
    }
  }, [foldersStoryCountQuery.data]);

  useEffect(() => {
    if (selectedSectionId && entityId) {
      void foldersStoryCountQuery.refetch();
    }
  }, [selectedSectionId, entityId, stories]);

  useEffect(() => {
    updateHeaderText();
  }, [showReports, isLastPathCreateTemplate, selectedSectionId, currentSectionName]);

  useEffect(() => {
    setSelectedStories(Map([]));
    setSelectAllStatus(ESelectAllStatus.None);
    selectionCtx.clearSelection();
  }, [props.lastPath, rowsPerPageReportEmbed]);

  useEffect(() => {
    setShowReports(selectedSectionLabel === REPORTS);
    updateHeaderText();
  }, [publication.id, selectedSectionLabel]);

  useEffect(() => {
    if (publication.id && entityId && user.uuid) {
      sendPublicationViewedActivity(publication, user.uuid, entityId);
    }
  }, [publication.id]);

  useEffect(() => {
    if (publicationCookie) {
      if (!selectedSectionId && publicationCookie.sort) {
        setSort(publicationCookie.sort);
      }
      if (publicationCookie.storyVisibility) {
        setStoryVisibility(publicationCookie.storyVisibility);
      }
      if (publicationCookie.view) {
        setView(publicationCookie.view);
      }
      if (publicationCookie.show) {
        setShow(publicationCookie.show);
      }
      if (publicationCookie.storyAccess) {
        try {
          const parsedAccess =
            typeof publicationCookie.storyAccess === 'string'
              ? JSON.parse(publicationCookie.storyAccess)
              : publicationCookie.storyAccess;
          setStoryAccess(parsedAccess);
        } catch (e) {
          console.error('Error parsing storyAccess from cookie in useEffect:', e);
          setStoryAccess([STORIES_VIEWER_PRIVILEGE]);
        }
      }
    }
    // No imperative fetch here anymore — the cookie-restored
    // sort/show/visibility/storyAccess state above feeds the declarative
    // `storiesParams` memo, which drives `usePublicationStories`.
  }, []);
  // Data fetching is declarative now (storiesParams /
  // templatesParams memos drive the queries). This effect only preserves the
  // view-side effect the imperative version had: hide the reports panel when
  // landing on the create-template view or on a selected section.
  useEffect(() => {
    if (!entityId) return;
    if (isLastPathCreateTemplate) {
      setShowReports(false);
    } else if (
      TAB_STORIES === lastPath ||
      !isEmpty(selectedSectionId) ||
      (lastPath === TAB_REPORTS && displayReportEmbeds)
    ) {
      if (!isEmpty(selectedSectionId)) {
        setShowReports(false);
      }
    }
  }, [props.lastPath, lastPath, isLastPathCreateTemplate, selectedSectionId, displayReportEmbeds, entityId]);

  const subscribeExportPdfMessages = () => {

    const onMessageReceived: MessageListener = (messageData) => {
      const latestExportPdfProcess: IExportPdfProcess = messageData?.data?.exportFolderPdfRequests[0];
      setExportPdfProcess(latestExportPdfProcess || null);
    };
    exportPdfSubscription.addMessageListener(onMessageReceived);
    exportPdfSubscription.subscribe({ variables: { folderId: selectedSectionId } });
  };

  const subscribePublicationExportPdfMessages = () => {
    const onMessageReceived: MessageListener = (messageData) => {
      const latestExportPdfProcess: IExportPublicationPdfProcess = messageData?.data?.publicationExportPdfRequest[0];
      setExportPublicationPdfProcess(latestExportPdfProcess || null);
    };
    exportPublicationPdfSubscription.addMessageListener(onMessageReceived);
    exportPublicationPdfSubscription.subscribe({ variables: { publicationId: publication.id } });
  };

  //Export PDF subscription
  useEffect(() => {
    if (!isAdmin) return;
    setExportPublicationPdfProcess(null);
    if (!publication.id) return;
    // Listen for messages
    exportPublicationPdfSubscription.clearAllMessageListeners();
    subscribePublicationExportPdfMessages();

    return () => {
      exportPublicationPdfSubscription.unsubscribe();
    };
  }, [publication.id, entityId, entitySubdomain, isAdmin]);

  useEffect(() => {
    setExportPdfProcess(null);
    if (!selectedSectionId) return;
    // Listen for messages
    exportPdfSubscription.clearAllMessageListeners();
    subscribeExportPdfMessages();

    return () => {
      exportPdfSubscription.unsubscribe();
    };
  }, [selectedSectionId, entityId, entitySubdomain]);

  //Async job pdf subscription
  useEffect(() => {
    if (!isAdmin) return;
    setExportPublicationPdfProcess(null);
    if (!publication.id) return;
    // Listen for messages
    exportPublicationPdfSubscription.clearAllMessageListeners();
    subscribePublicationExportPdfMessages();

    return () => {
      exportPublicationPdfSubscription.unsubscribe();
    };
  }, [publication.id, entityId, entitySubdomain, isAdmin]);

  const exportPdfInlineMessages = () => {
    const inProgressMessage = !!selectedSectionId &&
      exportPdfProcess?.status === EProcessStatus.InProgress &&
      exportPdfProcess?.suppressDisplayBanner && (
        <Box data-test="export-pdf-in-progress-message" sx={styles.exportPdfInProgress}>
          <CircularProgress size={16} data-test="loading-spinner" />
          <span>{storyMessages.buildingThePDF}</span>
        </Box>
      );

    const successMessage = !!selectedSectionId &&
      exportPdfProcess?.status === EProcessStatus.Success &&
      exportPdfProcess?.suppressDisplayBanner && (
        <Box sx={styles.exportPdfSuccess}>
          <CheckIcon sx={styles.exportPdfCheckIcon} />
          {exportPdfMessage(exportPdfProcess.createdAt, storyMessages, exportPdfProcess.id, false)}
        </Box>
      );

    return [inProgressMessage, successMessage];
  };

  const exportPublicationPdfInlineMessages = () => {
    const inProgressMessage = !!publication.id &&
      exportPublicationPdfProcess?.status === EProcessStatus.InProgress &&
      exportPublicationPdfProcess?.suppressDisplayBanner && (
        <Box role="status" aria-live="polite" data-test="publication-export-pdf-in-progress-message" sx={styles.exportPublicationPdfInProgress}>
          <CircularProgress size={16} data-test="loading-spinner" />
          <span>{storyMessages.buildingThePDF}</span>
        </Box>
      );
    const successMessage = !!publication.id &&
      exportPublicationPdfProcess?.status === EProcessStatus.Success &&
      exportPublicationPdfProcess?.suppressDisplayBanner && (
        <Box data-test="publication-export-pdf-success-message" sx={styles.exportPublicationPdfSuccess}>
          <Typography variant="body2" color="text.secondary">
            {messages.pdfGenerated} {formatCreatedAt(exportPublicationPdfProcess.createdAt)}
          </Typography>
          <Box sx={styles.exportPublicationPdfDownload}>
            <a
              target="_blank"
              href={`/api/stories/v1/export-requests/${exportPublicationPdfProcess.id
                }/isPublication/${true}/download-pdf`}
              rel="noopener noreferrer"
              download={true}
              aria-label={messages.downloadFile}
            >
              <Button variant="outlined" size="small" startIcon={<FileDownloadOutlinedIcon />}>
                {messages.downloadFile}
              </Button>
            </a>
          </Box>
        </Box>
      );

    return [inProgressMessage, successMessage];
  };

  const setSelectedSection = async (section: IStoryListSelection | null, viewtype?: string) => {
    if (viewtype === TAB_REPORTS) {
      setSort(SORT_UPDATED_NEW_TO_OLD);
      setShowReports(true);
      navigateToPublicationReportsView(entitySubdomain, publication?.id);
    } else if (viewtype === TAB_CUSTOM_REPORTS) {
      navigateToPublicationCustomReportsView(entitySubdomain, publication?.id ?? '');
    } else if (viewtype === STORIES) {
      setSort(SORT_UPDATED_NEW_TO_OLD);
      setShowReports(false);
      setSelectedSectionIdState(null);
      navigateToPublicationView(entitySubdomain, publication?.id ?? '');
    } else {
      setShowReports(false);
      if ((!section && !selectedSectionId && !showReports) || section?.id === selectedSectionId) return;
      if (section) {
        setSort(SORT_CUSTOM);
        setSelectedSectionIdState(section.id);
        navigateToPublicationFolderList(entitySubdomain, publication?.id, section.id);
      } else {
        setSort(SORT_UPDATED_NEW_TO_OLD);
        setSelectedSectionIdState(null);
        navigateToPublicationView(entitySubdomain, publication?.id);
      }
    }
  };

  const subscribeFolderCopyProcessMessages = () => {
    const onMessageReceived: MessageListener = (messageData) => {
      const folderProcess: IFolderCopyProcess = messageData?.data?.folderProcess;
      if (folderProcess?.status === EProcessStatus.Success) {
        if (folderProcess?.suppressDisplayBanner) {
          copyFolderSubscription.unsubscribe();
        }
        setFolderCopyProcess(folderProcess);
        const pubId = publication?.id;
        if (pubId) {
          void foldersHierarchyQuery.refetch();
          navigateToPublicationFolderList(entitySubdomain, publication.id, selectedSectionId);
          void storiesQuery.refetch();
        }
      } else if (folderProcess?.status === EProcessStatus.Error && folderProcess?.suppressDisplayBanner) {
        setFolderCopyProcess(folderProcess);
        copyFolderSubscription.unsubscribe();
      } else if (!folderProcess) {
        copyFolderSubscription.unsubscribe();
      } else {
        setFolderCopyProcess(folderProcess || null);
      }
    };
    copyFolderSubscription.addMessageListener(onMessageReceived);
    copyFolderSubscription.subscribe({ variables: { folderId: selectedSectionId } });
  };

  const subscribeStoriesCopyProcessMessages = () => {
    const onMessageReceived: MessageListener = (messageData) => {
      const storiesProcesses: IStoriesCopyProcess[] = messageData?.data?.storiesProcesses;
      const latestStoriesProcess = storiesProcesses[0];
      if (latestStoriesProcess?.status === EProcessStatus.Success) {
        if (!latestStoriesProcess?.suppressDisplayBanner) {
          const pubId = publication?.id;
          if (pubId) {
            navigateToPublicationFolderList(entitySubdomain, publication.id, selectedSectionId);
          }
          void storiesQuery.refetch();
          setHighlightedStoriesIds(latestStoriesProcess.storyCopyProcesses.map(({ storyCopyId }) => storyCopyId));
          highlightFadeTimeout.current = setTimeout(() => {
            setHighlightedStoriesIds([]);
          }, SLOW_FADE_HIGHLIGHT_TIMEOUT);
        } else if (latestStoriesProcess?.suppressDisplayBanner) {
          copyStoriesSubscription.unsubscribe();
        }
      } else if (latestStoriesProcess?.status === EProcessStatus.Error && latestStoriesProcess?.suppressDisplayBanner) {
        copyStoriesSubscription.unsubscribe();
      } else if (!latestStoriesProcess) {
        copyStoriesSubscription.unsubscribe();
      }
      setStoriesCopyProcesses(storiesProcesses);
    };
    copyStoriesSubscription.addMessageListener(onMessageReceived);
    copyStoriesSubscription.subscribe({ variables: { folderId: selectedSectionId } });
  };

  const subscribePublicationCopyProcessMessages = () => {
    const onMessageReceived: MessageListener = (messageData) => {
      const publicationProcess: string = messageData?.data?.asyncJobs[0]?.jobStatus;
      if (publicationProcess === AsyncJobStatus.Success || publicationProcess === AsyncJobStatus.Error) {
        copyPublicationSubscription.unsubscribe();
        if (publicationProcess === AsyncJobStatus.Success) {
          setShouldNavigateToCopiedPublication(true);
        }
      }
      if (publicationProcess) {
        setCopyPublicationProcessStatus(publicationProcess);
      }
    };
    copyPublicationSubscription.addMessageListener(onMessageReceived);
    copyPublicationSubscription.subscribe({ variables: { copyPublicationJobId } });
  };

  //Folder copy process
  useEffect(() => {
    setFolderCopyProcess(null);
    if (!selectedSectionId) return;
    copyFolderSubscription.clearAllMessageListeners();
    subscribeFolderCopyProcessMessages();
    return () => {
      copyFolderSubscription.unsubscribe();
    };
  }, [selectedSectionId, entityId, entitySubdomain]);

  //Stories copy process
  useEffect(() => {
    setStoriesCopyProcesses([]);
    setHighlightedStoriesIds([]);
    clearTimeout(highlightFadeTimeout.current);
    if (!selectedSectionId) return;
    copyStoriesSubscription.clearAllMessageListeners();
    subscribeStoriesCopyProcessMessages();

    return () => {
      copyStoriesSubscription.unsubscribe();
    };
  }, [selectedSectionId, entityId, entitySubdomain]);

  useEffect(() => {
    if (!copyPublicationJobId) return;
    copyPublicationSubscription.clearAllMessageListeners();
    subscribePublicationCopyProcessMessages();
  }, [copyPublicationJobId]);

  useEffect(() => {
    if (shouldNavigateToCopiedPublication && publication?.id && entitySubdomain) {
      setShouldNavigateToCopiedPublication(false);
      navigateToPublicationView(entitySubdomain, publication.id);
    }
  }, [shouldNavigateToCopiedPublication, publication?.id, entitySubdomain]);

  useEffect(() => {
    if (copyPublicationProcessStatus === AsyncJobStatus.Success) {
      const publicationTitle = publication?.title;
      if (publicationTitle) {
        toggleToast({
          title: messages.publicationCopied,
          body: messages.publicationCopiedDescription({
            publication: publicationTitle
          }),
          severity: 'success'
        });
      }
    }
    if (copyPublicationProcessStatus === AsyncJobStatus.InProgress) {
      toggleToast({
        title: messages.publicationCopying,
        body: messages.publicationCopyingDescription,
        severity: 'info'
      });
    }
    if (copyPublicationProcessStatus === AsyncJobStatus.Error) {
      toggleToast({
        title: messages.publicationCopyFailed,
        body: messages.publicationCopyFailedDescription,
        severity: 'error'
      });
    }
  }, [copyPublicationProcessStatus, publication.title]);

  useEffect(() => {
    return () => {
      copyPublicationSubscription.unsubscribe();
    };
  }, []);

  const subscribeBulkStatusChangeProcessMessages = () => {
    const onMessageReceived: MessageListener = (messageData) => {
      const publicationProcess: string = messageData?.data?.asyncJobs[0]?.jobStatus;
      if (publicationProcess === AsyncJobStatus.Success || publicationProcess === AsyncJobStatus.Error) {
        bulkStatusChangeSubscription.unsubscribe();
      }
      if (publicationProcess) {
        setBulkChangeStatusProcessStatus(publicationProcess);
      }
    };
    bulkStatusChangeSubscription.addMessageListener(onMessageReceived);
    bulkStatusChangeSubscription.subscribe({ variables: { bulkChangeStatusJobId } });
  };

  useEffect(() => {
    if (!bulkChangeStatusJobId) return;
    bulkStatusChangeSubscription.clearAllMessageListeners();
    subscribeBulkStatusChangeProcessMessages();
  }, [bulkChangeStatusJobId]);

  useEffect(() => {
    if (bulkChangeStatusProcessStatus === AsyncJobStatus.Success) {
      setSelectAllStatus(ESelectAllStatus.None);
      selectionCtx.setBulkChangeStatusJobId(null);
      setBulkChangeStatusProcessStatus(null);
      const publicationTitle = publication?.title;
      if (publicationTitle) {
        if (publication.status === STATE_PUBLISHED) {
          generateOnlinePublicationUrl({
            publication,
            userId: user.id,
            update: false,
            enableGoogleTranslateForPublications,
            isPreview: false,
            entitySubdomain
          }).catch(() => undefined);
        }
        void publicationByIdQuery.refetch();
        void storiesQuery.refetch();
        toggleToast({
          title: messages.storyStatusChanged,
          body: messages.storyStatusChangedDescription,
          severity: 'success'
        });
      }
    }
    if (bulkChangeStatusProcessStatus === AsyncJobStatus.Error) {
      const publicationTitle = publication?.title;
      if (publicationTitle) {
        setLocalStorageParsedValue(publication.id, { bulkChangeStatusFailureBanner: true });
      }
    }
  }, [bulkChangeStatusProcessStatus, selectedSectionId]);

  useEffect(() => {
    return () => {
      bulkStatusChangeSubscription.unsubscribe();
    };
  }, []);

  const setPage = (value: number) => {
    setSelectedStories(Map([]));
    if (value !== 0) setCurrentPage(value);
    if (!selectAllStories) {
      setSelectAllStatus(ESelectAllStatus.None);
    }
  };

  const handleStoryRowsPerPageChange = (value: number): void => {
    setSelectedStories(Map([]));
    setRowsPerPage(value);
    if (!selectAllStories) {
      setSelectAllStatus(ESelectAllStatus.None);
    }
  };

  const handleClick = (story: IStory, isTemplate: boolean) => {
    if (lastPath === PATH_CREATE_FROM_TEMPLATE) {
      setSelectedTemplate(story);
      setIsCreateFromTemplateModalVisible(true);
    } else {
      navigateToPublicationStoryView(story._id, isTemplate, publication.id);
    }
  };

  const navigateToOrganizeSectionsAndStoriesPage = () => {
    if (publicationFolderStructure?.length) {
      const firstSectionId = publicationFolderStructure[0].id;
      navigateToOrganizeSectionAndStoriesPath(entitySubdomain, publication.id, rootFolderId, firstSectionId);
    }
  };

  const navigateToSetupOnlinePublicationPage = () => {
    if (publication?.id) {
      navigateToSetupOnlinePublication(publication.id);
    }
  };

  const renderModals = () => {
    const createFromTemplateModal =
      isCreateFromTemplateModalVisible &&
      createPortal(
        <CreateStoryFromTemplateModal
          isAdmin={isAdmin}
          canOrganizeStories={canOrganizeStories}
          currentFolderId={selectedSectionId}
          folderStructure={publicationFolderStructure}
          messages={storyMessages}
          entityName={entityName}
          entityId={entityId}
          entitySubdomain={entitySubdomain}
          settings={settings}
          user={user}
          publication={publication}
          onClose={() => setIsCreateFromTemplateModalVisible(false)}
          selectedTemplate={selectedTemplate}
        />,
        document.getElementById('app')
      );

    const deleteModal =
      isDeleteModalVisible &&
      createPortal(
        <PublicationDeleteModal
          messages={messages}
          selectedSectionId={selectedSectionId}
          isDeleteSection={isSectionDeleteAction}
          selectedPublicationId={publication.id}
          publicationName={publication.title}
          publicationUrl={publication.publicUrl}
          onClose={() => {
            setIsSectionDeleteAction(false);
            setIsDeleteModalVisible(false);
          }}
          entityId={entityId}
          entityUuid={entityUuid}
          entitySubdomain={entitySubdomain}
          userId={user.id}
          isAdmin={isAdmin}
          publicationFolderStructure={publicationFolderStructure}
          isPublicationEmpty={!publicationFolderStructureLength}
          isCustomSpreadsheetReportEnabled={isCustomSpreadsheetReportEnabled}
        />,
        document.getElementById('app')
      );

    const addSelectionModal =
      isCreatePublicationModalVisible &&
      createPortal(
        <AddSectionModal
          onClose={() => setIsCreatePublicationModalVisible(false)}
          messages={messages}
          folderStructure={publicationFolderStructure}
          publication={publication}
          user={user}
          entityId={entityId}
          isAdmin={isAdmin}
          rootFolderId={publicationRootFolderId}
          selectedSectionId={selectedSectionId}
          entitySubdomain={entitySubdomain}
        />,
        document.getElementById('app')
      );

    const copyFolderModal =
      isCopyFolderModalVisible &&
      createPortal(
        <CopyFolderModal
          currentFolderId={selectedSectionId}
          currentFolderName={currentSectionName}
          folderToCopy={{ id: selectedSectionId, displayName: currentSectionName }}
          publicationFolders={publicationFolders}
          folderStructure={folderStructure}
          publicationFolderStructure={publicationFolderStructure ?? []} // Story 4 Phase B: hook data
          messages={storyMessages}
          entityId={entityId}
          entitySubdomain={entitySubdomain}
          rootFolderId={rootFolderId}
          onClose={() => setIsCopyFolderModalVisible(false)}
          isFromPublication={true}
          publicationId={publication?.id}
          userId={user.id}
          publicationViewMessages={messages}
          setSection={setSelectedSection}
        />,
        document.getElementById('app')
      );
    const publicationExportModal =
      isPublicationExportPdfModalVisible &&
      createPortal(
        <ExportPdfModal
          publicationId={publication.id}
          messages={modalMessages}
          storyMarginsEnabled={storyMarginsEnabled}
          getPdfPreviewLink={getPublicationPdfPreviewLink}
          isPreviewingPdfFailure={isPublicationPreviewingPdfFailure}
          previewFolderPdfError={previewPublicationPdfError}
          isPreviewingPdf={isPreviewingPublicationPdf}
          isPublicationExport={true}
          publicationModalMessages={publicationModalMessages}
          exportPublicationPdf={({ publicationId: pubId, pdfOptions }) =>
            exportPublicationPdfMutation.mutateAsync({ publicationId: pubId, entityId, pdfOptions })
          }
          onClose={() => setIsPublicationExportPdfModalVisible(false)}
        />,
        document.getElementById('app')
      );

    const publicationSectionUpdateModal =
      isUpdatePublicationSectionModalVisible &&
      createPortal(
        <UpdatePublicationSectionModal
          isAdmin={isAdmin}
          userId={user.id}
          entityId={entityId}
          publicationModalMessages={publicationModalMessages}
          selectedSectionId={selectedSectionId}
          publicationId={publication?.id}
          selectedSectionName={currentSectionName}
          onClose={() => setUpdatePublicationSectionModalVisible(false)}
        />,
        document.getElementById('app')
      );

    const moveFolderModal =
      isMoveFolderModalVisible &&
      canOrganizeStories &&
      createPortal(
        <MoveFolderModal
          folderToMove={{ id: selectedSectionId, displayName: currentSectionName }}
          folderStructure={publicationFolderStructure}
          messages={storyMessages}
          entityId={entityId}
          rootFolderId={publicationRootFolderId}
          onClose={() => setIsMoveFolderModalVisible(false)}
          moveSections={moveSections}
          publicationViewMessages={messages}
          isFromPublication={true}
          publicationId={publication.id}
          userId={user.id}
        />,
        document.getElementById('app')
      );
    return [
      deleteModal,
      addSelectionModal,
      copyFolderModal,
      createFromTemplateModal,
      publicationSectionUpdateModal,
      moveFolderModal,
      publicationExportModal
    ];
  };

  const updateSort = (sortVal: ISortOptions) => {
    setSort(sortVal);
    cookies.set('publications', getNewCookieValue(cookies.get('publications'), SORT, sortVal), { path: '/' });
  };

  const updateView = (viewVal: IStoryViewOptions) => {
    setView(viewVal);
    cookies.set('publications', getNewCookieValue(cookies.get('publications'), VIEW, viewVal), { path: '/' });
  };

  const updateShow = (showVal: IStoryShowOptions) => {
    setShow(showVal);
    cookies.set('publications', getNewCookieValue(cookies.get('publications'), SHOW, showVal), { path: '/' });
  };

  const updateStoryVisibility = (storyVisibilityVal: IStoryVisibilityOptions) => {
    setStoryVisibility(storyVisibilityVal);
    cookies.set('publications', getNewCookieValue(cookies.get('publications'), STORY_VISIBILITY, storyVisibilityVal), { path: '/' });
  };

  const updateStoryAccess = (storyAccessVal: IStoryPrivilege[]) => {
    setStoryAccess(storyAccessVal);
    cookies.set(
      'publications',
      getNewCookieValue(cookies.get('publications'), STORY_ACCESS, JSON.stringify(storyAccessVal)),
      { path: '/' }
    );
  };

  const clearSearchFilters = (): void => {
    setSort(SORT_UPDATED_NEW_TO_OLD);
    setView(VIEW_LIST);
    setShow(SHOW_ALL);
    setStoryVisibility(STATE_ALL);
    setStoryAccess([STORIES_VIEWER_PRIVILEGE]);
    setSearch('');
    cookies.set('publications', getNewCookieValue(cookies.get('publications'), SORT, SORT_UPDATED_NEW_TO_OLD), { path: '/' });
    cookies.set('publications', getNewCookieValue(cookies.get('publications'), VIEW, VIEW_LIST), { path: '/' });
    cookies.set('publications', getNewCookieValue(cookies.get('publications'), SHOW, SHOW_ALL), { path: '/' });
    cookies.set('publications', getNewCookieValue(cookies.get('publications'), STORY_VISIBILITY, storyVisibility), { path: '/' });
    cookies.set(
      'publications',
      getNewCookieValue(cookies.get('publications'), STORY_ACCESS, JSON.stringify(storyAccess)),
      { path: '/' }
    );
  };

  const renderStoryContent = (): JSX.Element => {
    const itemName = isLastPathCreateTemplate ? storyMessages.templates : storyMessages.stories;
    const totals = isLastPathCreateTemplate ? templates.total : stories?.total;
    const hasItemsLoaded = isLastPathCreateTemplate ? templates.total > 0 : stories.total > 0;
    return (
      <div>
        <PublicationReportEmbedContent
          entityId={entityId}
          entitySubdomain={entitySubdomain}
          entityUuid={entityUuid}
          messages={messages}
          modalMessages={modalMessages}
          storyMessages={storyMessages}
          publication={publication}
          user={user}
          isAdmin={isAdmin}
          settings={settings}
          isReportEmbedListSelected={props.isReportEmbedListSelected}
          selectedSectionId={selectedSectionId}
          showReports={showReports}
          rowsPerPageReportEmbed={rowsPerPageReportEmbed}
          setSelectedSection={setSelectedSection}
          setRowsPerPageReportEmbed={setRowsPerPageReportEmbed}
          setIsCreatePublicationModalVisible={setIsCreatePublicationModalVisible}
          setView={setView}
          reports={reports}
          isNewReportTileModalEnabled={isNewReportTileModalEnabled}
        />
        <Box
          sx={showReports || (filterSelected && !hasItemsLoaded) || isLoading ? styles.hide : {}}
          data-test="publication-folder-stories-list"
        >
          {view === VIEW_GRID ? (
            <StoryCardGrid
              showGlobalNavBar={showGlobalNavBar}
              stories={isLastPathCreateTemplate ? templates.data : stories.data}
              storiesUrl={stories.storiesUrl}
              isTemplate={isLastPathCreateTemplate ? true : false}
              messages={storyMessages}
              onClick={handleClick}
              paginationOptions={{
                totalRows: totals,
                rowsPerPage,
                setRowsPerPage: handleStoryRowsPerPageChange,
                currentPage,
                setPage,
                itemName
              }}
            />
          ) : (
            <StoryListTable
              showGlobalNavBar={showGlobalNavBar}
              bulkChangeStatusProcessStatus={bulkChangeStatusProcessStatus}
              publicationSelectedStories={selectedStories}
              publicationSetSelectedStories={setSelectedStories}
              publicationSelectAllStatus={selectAllStatus}
              publicationSetSelectAllStatus={setSelectAllStatus}
              publicationStoryListing={true}
              stories={isLastPathCreateTemplate ? templates.data : stories.data}
              messages={storyMessages}
              isTemplate={isLastPathCreateTemplate ? true : false}
              isSelectable={isLastPathCreateTemplate ? false : isAdmin}
              onClick={handleClick} // To be fixed in manage publications
              folderStructure={publicationFolderStructure}
              isViewingFolder={!!selectedSectionId}
              entitySubdomain={entitySubdomain}
              setSelectedList={() => { }} // To be fixed in manage publications
              canOrganizeStories={isAdmin}
              publicationId={publication?.id}
              highlightedPublicationStoriesIds={highlightedStoriesIds}
              selectAllStories={selectAllStories}
              totalStoriesCount={totalStoriesCount}
              paginationOptions={{
                totalRows: totals,
                rowsPerPage,
                setRowsPerPage: handleStoryRowsPerPageChange,
                currentPage,
                setPage,
                itemName
              }}
            />
          )}

        </Box>

        <EmptyViewContainer shouldShow={!showReports && filterSelected && !hasItemsLoaded}>
          <StoriesListEmptyView
            storyMessages={storyMessages}
            selectedSectionId={selectedSectionId}
            clearSearchFilters={clearSearchFilters}
            setSelectedSection={setSelectedSection}
          />
        </EmptyViewContainer>
      </div>
    );
  };

  const renderContent = (): JSX.Element => {
    const showButton =
      isAdmin && !showReports && !publicationFolderStructureLength && !isFoldersLoading && !showStoriesMessage;

    const showEmptyContent =
      (!publicationFolderStructureLength || showStoriesMessage) &&
      !isFoldersLoading &&
      !isLastPathCreateTemplate &&
      !filterSelected &&
      !showReports;

    let heading = isAdmin ? messages.welcomeToPublications : messages.welcomeToPublicationsNonAdmin;
    let subHeading = isAdmin ? messages.addSectionDescription : messages.addSectionDescriptionNonAdmin;
    if (showReports) {
      heading = messages.welcomeToPublicationReports;
      subHeading = messages.addReportEmbedsInPublication;
    } else if (showStoriesMessage) {
      heading = isAdmin ? messages.welcomeToSection : messages.welcomeToSectionNonAdmin;
      subHeading = isAdmin ? messages.addStoriesInSection : messages.addStoriesInSectionNonAdmin;
    }

    return showEmptyContent ? (
      <EmptyViewContent
        heading={heading}
        subHeading={subHeading}
        showButton={showButton}
        messages={messages}
        setIsCreatePublicationModalVisible={setIsCreatePublicationModalVisible}
      />
    ) : (
      renderStoryContent()
    );
  };

  const organizeSectionHeader = () => (
    <Box sx={styles.folderContentHeader}>
      <Box sx={styles.hideOverflow}>
        <Box sx={styles.folderTitle}>
          <Typography
            variant="h2"
            component="h2"
            sx={styles.sectionHeaderText}
          >
            {pageHeader ? pageHeader : messages.allPublicationStories}
          </Typography>
          {isAdmin && selectedSectionId && currentSectionName && !isLastPathCreateTemplate && (
            <Button
              size="small"
              variant="text"
              aria-label="edit"
              data-test="folder-rename-button"
              onClick={() => setUpdatePublicationSectionModalVisible(true)}
              startIcon={<EditOutlinedIcon fontSize="small" />}
              sx={styles.editButton}
            >
              {messages.edit}
            </Button>
          )}
        </Box>
        {selectedSectionId &&
          !isLastPathCreateTemplate &&
          (storiesAccessControlEnabled === false ||
            (storiesAccessControlEnabled &&
              Array.isArray(currentSubjectPublicationStoryClaim) &&
              currentSubjectPublicationStoryClaim.includes(STORIES_ADMIN_PRIVILEGE))) && (
            <Typography variant="caption" color="text.secondary" data-test="stories-count-text">
              {`${messages.storiesCountDescription} ${allStoriesCount}`}
            </Typography>
          )}
        {isLastPathCreateTemplate && (
          <Typography variant="caption" color="text.secondary" data-test="stories-count-text">
            {storyMessages.templateDescription}
          </Typography>
        )}
      </Box>
      {selectedSectionId && !isLastPathCreateTemplate && (
        <Box sx={styles.buttonContainer}>
          <Box sx={styles.exportPdfInlineMessagesContainer}>
            {exportPdfInlineMessages()}
          </Box>
          <Box sx={styles.sectionButtonsContainer}>
            {isAdmin && selectedSectionId && (
              <Button
                variant="outlined"
                size="small"
                data-test="section-export-pdf-button"
                onClick={() => navigateToSectionSetupPDF(entitySubdomain, selectedSectionId)}
                disabled={exportPdfProcess?.status === EProcessStatus.InProgress}
              >
                {messages.exportSection}
              </Button>
            )}
            {isAdmin && selectedSectionId && (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  data-test="manage-section-dropdown"
                  onClick={(e) => setManageSectionAnchorEl(e.currentTarget)}
                  endIcon={<ArrowDropDownIcon />}
                >
                  {messages.moreActions}
                </Button>
                <Menu
                  id="manage-section-dropdown"
                  anchorEl={manageSectionAnchorEl}
                  open={Boolean(manageSectionAnchorEl)}
                  onClose={() => setManageSectionAnchorEl(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <MenuItem
                    data-test="move-section-button"
                    onClick={() => { setManageSectionAnchorEl(null); setIsMoveFolderModalVisible(true); }}
                  >
                    {messages.move}
                  </MenuItem>
                  <MenuItem
                    data-test="copy-section-button"
                    onClick={() => { setManageSectionAnchorEl(null); setIsCopyFolderModalVisible(true); }}
                  >
                    {messages.copySection}
                  </MenuItem>
                  <MenuItem
                    data-test="delete-section-button"
                    sx={styles.deleteSectionMenuItem}
                    onClick={() => {
                      setManageSectionAnchorEl(null);
                      setIsSectionDeleteAction(true);
                      setIsDeleteModalVisible(true);
                    }}
                  >
                    {messages.deleteSection}
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );

  const renderStoriesControlBar = () => {
    if (showReports) return null;
    return (
      <Box sx={styles.controlBarContainer} data-test="publication-stories-control-bar">
        {selectedStories.size > 0 || selectAllStories ? (
          <SelectionToolbar
            publicationId={publication?.id}
            entityId={entityId}
            entityUuid={entityUuid}
            entitySubdomain={entitySubdomain}
            canCopyIndividualStories={
              Array.isArray(currentSubjectPublicationStoryClaim) &&
              currentSubjectPublicationStoryClaim.includes(PUBLICATIONS_MANAGER_PRIVILEGE)
            }
            currentFolderId={selectedSectionId}
            folderStructure={folderStructure}
            publicationFolderStructure={publicationFolderStructure}
            publicationFoldersTree={publicationFoldersTree}
            messages={storyMessages}
            subjectClaimsMessages={subjectClaimsMessages}
            selectAllStories={selectAllStories}
            totalStoriesCount={totalStoriesCount}
            currentPageStoriesCount={stories.data.length}
            sectionId={selectedSectionId}
            searchText={search}
            storyState={storyVisibility}
            show={show}
            publicationSelectedStories={selectedStories}
            publicationSetSelectedStories={setSelectedStories}
            publicationSetSelectAllStatus={setSelectAllStatus}
            publicationStoryListing={true}
            userId={user.id}
            isBulkUpdateStoryStatusEnabled={isBulkUpdateStoryStatusEnabled}
            setSectionId={setSelectedSection}
            storiesAccessControlEnabled={storiesAccessControlEnabled}
            isAdmin={isAdmin}
            openModal={openModal}
            canFollowStories={canFollowStories}
          />
        ) : (
          <ControlBar
            lastPath={lastPath}
            messages={storyMessages}
            publicationMessages={messages}
            search={search}
            show={show}
            sort={sort}
            currentFolderId={selectedSectionId}
            storyVisibility={storyVisibility}
            tab={tab}
            view={view}
            updateSearch={setSearch}
            updateShow={updateShow}
            updateSort={updateSort}
            updateStoryVisibility={updateStoryVisibility}
            updateView={updateView}
            storyAccess={storyAccess}
            updateStoryAccess={updateStoryAccess}
            publicationView={true}
            currentSubjectPublicationStoryClaim={currentSubjectPublicationStoryClaim}
          />
        )}
      </Box>
    );
  };

  // isFetchingPublicationReports removed — PublicationReportEmbedContent
  // owns its own loading state after the hook migration.
  const showLoadingSpinner =
    (!!copyPublicationProcessStatus &&
      copyPublicationProcessStatus !== AsyncJobStatus.Success &&
      copyPublicationProcessStatus !== AsyncJobStatus.Error) ||
    isLoading ||
    isFoldersLoading ||
    isStoriesLoading;

  return (
    <WorkspaceSidebarProvider
      entitySubdomain={props.entitySubdomain}
      entityUuid={props.entityUuid}
      sx={styles.container}
      dataTest="publication-view-container"
      recentActivitiesSource="stories"
      formsAndWorkflowsFeatureEnabled={isFormsAndWorkflowsFeatureEnabled}
      performanceManagementFeatureEnabled={isPerformanceManagementFeatureEnabled}
    >
      <Box component="main" data-test="publication-view-container" sx={styles.publicationView}>
        {renderModals()}
        <PublicationViewHeader
          copyPublicationJobId={copyPublicationJobId}
          messages={messages}
          publicationModalMessages={publicationModalMessages}
          title={publication.title}
          isCreateFromTemplate={isLastPathCreateTemplate}
          isPublicationCustomCssEnabled={isPublicationCustomCssEnabled}
          currentSubjectClaims={currentSubjectClaims}
          isPublicationSetupPdfEnabled={isPublicationSetupPdfEnabled}
          isBulkUpdateStoryStatusEnabled={isBulkUpdateStoryStatusEnabled}
          setIsDeleteModalVisible={setIsDeleteModalVisible}
          navigateToPublicationList={navigateToPublicationList}
          isAdmin={isAdmin}
          currentFolderId={selectedSectionId}
          publicationFolderStructure={publicationFolderStructure}
          entityName={entityName}
          entityId={entityId}
          entityUuid={entityUuid}
          entitySubdomain={entitySubdomain}
          canOrganizeStories={canOrganizeStories}
          user={user}
          storyMessages={storyMessages}
          publication={publication}
          settings={settings}
          setPage={setPage}
          setIsPublicationExportPdfModalVisible={setIsPublicationExportPdfModalVisible}
          isExportDisabled={exportPublicationPdfProcess?.status === EProcessStatus.InProgress}
          totalStoriesCount={publicationStoriesCount}
          navigateToOrganizeSectionsAndStories={navigateToOrganizeSectionsAndStoriesPage}
          navigateToSetupOnlinePublication={navigateToSetupOnlinePublicationPage}
          navigateToPublicationSetupPDF={navigateToPublicationSetupPDF}
          updateThumbnail={updateThumbnail}
          storiesAccessControlEnabled={storiesAccessControlEnabled}
          subjectClaimsMessages={subjectClaimsMessages}
          openModal={openModal}
          canCreateStories={
            storiesAccessControlEnabled &&
            Array.isArray(currentSubjectPublicationStoryClaim) &&
            currentSubjectPublicationStoryClaim.includes(STORIES_CREATE_PRIVILEGE)
          }
          currentSubjectPublicationStoryClaim={currentSubjectPublicationStoryClaim}
        />
        <Box sx={styles.publicationExportBanner}>
          <StoryListBanner
            isAdmin={isAdmin}
            messages={storyMessages}
            exportPdfProcess={exportPublicationPdfProcess}
            isPublicationViewBanner={true}
            publicationMessages={messages}
          />
        </Box>
        <Box sx={styles.contentWrapper}>
          {!isLastPathCreateTemplate && (
            <PublicationSidebarContent
              messages={messages}
              publication={publication}
              copyPublicationJobId={copyPublicationJobId}
              selectedSectionId={selectedSectionId}
              entityId={entityId}
              isFoldersLoading={isFoldersLoading}
              publicationFolderStructure={publicationFolderStructure}
              isAdmin={isAdmin}
              storyMessages={storyMessages}
              entitySubdomain={entitySubdomain}
              displayReportEmbeds={displayReportEmbeds}
              selectedSectionLabel={selectedSectionLabel}
              user={user}
              enableGoogleTranslateForPublications={enableGoogleTranslateForPublications}
              setSelectedSection={setSelectedSection}
              setIsCreatePublicationModalVisible={setIsCreatePublicationModalVisible}
              exportPublicationPdfInlineMessages={exportPublicationPdfInlineMessages}
              storiesAccessControlEnabled={storiesAccessControlEnabled}
              currentSubjectClaims={currentSubjectClaims}
              currentSubjectPublicationStoryClaim={currentSubjectPublicationStoryClaim}
              isCustomSpreadsheetReportEnabled={isCustomSpreadsheetReportEnabled}
            />
          )}

          {selectedSectionLabel === CUSTOM_REPORTS ? (
            <CustomReportsBoundary publication={publication} />
          ) : (
            <Box sx={isLastPathCreateTemplate ? styles.publicationStoriesContainerWithPadding : styles.publicationStoriesContainer}>
              <>
                <Box sx={styles.banner}>
                  <StoryListBanner
                    isAdmin={isAdmin}
                    messages={storyMessages}
                    folderCopyProcess={folderCopyProcess}
                    storiesCopyProcesses={storiesCopyProcesses}
                    exportPdfProcess={exportPdfProcess}
                    isPublicationViewBanner={true}
                    isPublicationSectionExportBannerVisible={true}
                    publicationMessages={messages}
                  />
                  {(bulkChangeStatusProcessStatus ||
                    getLocalStorageParsedValue(publication.id)?.bulkChangeStatusFailureBanner) &&
                    bulkChangeStatusProcessStatus !== AsyncJobStatus.Success && (
                      <StoryListBanner
                        isAdmin={isAdmin}
                        messages={storyMessages}
                        bulkChangeStatusProcessStatus={bulkChangeStatusProcessStatus}
                        publicationMessages={messages}
                        bulkChangeStatusFailureBanner={
                          getLocalStorageParsedValue(publication.id)?.bulkChangeStatusFailureBanner as boolean
                        }
                        publicationId={publication?.id}
                      />
                    )}
                </Box>
                {organizeSectionHeader()}
                {renderStoriesControlBar()}
              </>
              <Box sx={styles.publicationStoriesContent}>
                <Box sx={!showLoadingSpinner ? styles.hide : {}}>
                  <Box sx={styles.centered} data-test="publication-story-list-loading-spinner">
                    <PublicationViewLoadingSkeleton />
                  </Box>
                </Box>

                <Box sx={showLoadingSpinner ? styles.hide : {}}>
                  {isExportPdfModalVisible &&
                    createPortal(
                      <ExportPdfModal
                        publicationModalMessages={publicationModalMessages}
                        folderId={selectedSectionId}
                        messages={modalMessages}
                        storyMarginsEnabled={storyMarginsEnabled}
                        getPdfPreviewLink={getPdfPreviewLink}
                        isPreviewingPdfFailure={isPreviewingPdfFailure}
                        previewFolderPdfError={previewFolderPdfError}
                        isPreviewingPdf={isPreviewingPdf}
                        isPublicationSectionExport={true}
                        onClose={() => setIsExportPdfModalVisible(false)}
                      />,
                      document.getElementById('app')
                    )}
                  {renderContent()}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </WorkspaceSidebarProvider>
  );
};

export function EmptyViewContainer({ shouldShow, children }: { shouldShow: boolean; children: React.ReactNode }) {
  return <Box sx={shouldShow ? {} : styles.hide}>{children}</Box>;
}

export const PublicationView = (props: IPublicationsViewProps) => {
  return (
    <PageErrorBoundary viewName={ViewName.PublicationViewPage}>
      <StoriesSelectionProvider>
        <PublicationViewInternal {...props} />
      </StoriesSelectionProvider>
    </PageErrorBoundary>
  );
};
